import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { Users, UserPlus, Star, X, Loader2 } from "lucide-react";
import FollowButton from "./FollowButton";

const FollowersList = ({ userId, token, currentUserId, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("followers");
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && userId) {
      fetchFollowData();
    }
  }, [isOpen, userId]);

  const fetchFollowData = async () => {
    setLoading(true);
    try {
      const [followersRes, followingRes] = await Promise.all([
        axios.get(`${API}/users/${userId}/followers`),
        axios.get(`${API}/users/${userId}/following`)
      ]);
      setFollowers(followersRes.data.followers || []);
      setFollowing(followingRes.data.following || []);
    } catch (err) {
      console.error("Error fetching follow data:", err);
      toast.error("Failed to load follow data");
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (id) => {
    onClose();
    navigate(`/profile/${id}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <Card className="w-full max-w-md max-h-[80vh] overflow-hidden bg-[#0A0A0A] border border-white/10">
        <CardHeader className="border-b border-white/10 flex flex-row items-center justify-between">
          <CardTitle className="font-bebas text-xl tracking-wider text-white">
            CONNECTIONS
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-[#A1A1AA] hover:text-white"
            data-testid="close-followers-modal"
          >
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full bg-[#0A0A0A] border-b border-white/10 rounded-none h-12">
              <TabsTrigger 
                value="followers" 
                className="flex-1 data-[state=active]:bg-[#7a6200]/20 data-[state=active]:text-[#7a6200]"
              >
                <Users className="w-4 h-4 mr-2" />
                Followers ({followers.length})
              </TabsTrigger>
              <TabsTrigger 
                value="following" 
                className="flex-1 data-[state=active]:bg-[#7a6200]/20 data-[state=active]:text-[#7a6200]"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Following ({following.length})
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#7a6200] animate-spin" />
              </div>
            ) : (
              <>
                <TabsContent value="followers" className="m-0 max-h-[400px] overflow-y-auto">
                  {followers.length === 0 ? (
                    <div className="text-center py-12 text-[#52525B]">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="font-ubuntu">No followers yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {followers.map((user) => (
                        <div 
                          key={user.id} 
                          className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                        >
                          <div 
                            className="flex items-center gap-3 cursor-pointer flex-1"
                            onClick={() => handleUserClick(user.id)}
                          >
                            <div className="w-10 h-10 rounded-full overflow-hidden border border-white/20">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.full_name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-[#7a6200]/20 flex items-center justify-center">
                                  <span className="text-[#7a6200] font-bebas">
                                    {user.full_name?.charAt(0) || "?"}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-white font-ubuntu text-sm hover:text-[#7a6200] transition-colors">
                                {user.full_name}
                              </p>
                              {user.rating > 0 && (
                                <div className="flex items-center gap-1 text-[#C6A300]">
                                  <Star className="w-3 h-3 fill-current" />
                                  <span className="text-xs">{user.rating}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {currentUserId && currentUserId !== user.id && (
                            <FollowButton 
                              userId={user.id} 
                              token={token}
                              onFollowChange={() => fetchFollowData()}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="following" className="m-0 max-h-[400px] overflow-y-auto">
                  {following.length === 0 ? (
                    <div className="text-center py-12 text-[#52525B]">
                      <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="font-ubuntu">Not following anyone yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {following.map((user) => (
                        <div 
                          key={user.id} 
                          className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                        >
                          <div 
                            className="flex items-center gap-3 cursor-pointer flex-1"
                            onClick={() => handleUserClick(user.id)}
                          >
                            <div className="w-10 h-10 rounded-full overflow-hidden border border-white/20">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.full_name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-[#7a6200]/20 flex items-center justify-center">
                                  <span className="text-[#7a6200] font-bebas">
                                    {user.full_name?.charAt(0) || "?"}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-white font-ubuntu text-sm hover:text-[#7a6200] transition-colors">
                                {user.full_name}
                              </p>
                              {user.rating > 0 && (
                                <div className="flex items-center gap-1 text-[#C6A300]">
                                  <Star className="w-3 h-3 fill-current" />
                                  <span className="text-xs">{user.rating}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {currentUserId && currentUserId !== user.id && (
                            <FollowButton 
                              userId={user.id} 
                              token={token}
                              onFollowChange={() => fetchFollowData()}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </>
            )}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default FollowersList;
